import { AvatarBoError } from './AvatarBoClient'
import type { FileIssue } from './rejection-messages'
import type { AvatarBoClient, RegisterOptionInput } from './types'

export type FileStatus = 'waiting' | 'uploading' | 'done' | 'failed'

export interface FileProgress {
  name: string
  status: FileStatus
  loaded: number
  total: number
}

export type UploadOutcome =
  | { ok: true }
  | { ok: false; stage: 'upload' | 'register'; issues: FileIssue[] }

export interface RunUploadOpts {
  client: AvatarBoClient
  files: File[]
  uploadId: string
  option: Omit<RegisterOptionInput, 'uploadId'>
  onFile: (progress: FileProgress) => void
  now?: () => number
}

/** A presigned PUT that failed because the 15-minute URL had expired, rather than for good. */
const isExpired = (error: unknown): boolean =>
  error instanceof AvatarBoError &&
  (error.status === 403 || error.code === 'upload_failed')

export async function runUpload({
  client,
  files,
  uploadId,
  option,
  onFile,
}: RunUploadOpts): Promise<UploadOutcome> {
  files.forEach((file) =>
    onFile({ name: file.name, status: 'waiting', loaded: 0, total: file.size }),
  )

  for (const file of files) {
    const report = (status: FileStatus, loaded: number) =>
      onFile({ name: file.name, status, loaded, total: file.size })

    report('uploading', 0)
    try {
      await putOnce(client, uploadId, file, (loaded) =>
        report('uploading', loaded),
      )
      report('done', file.size)
    } catch (error) {
      report('failed', 0)

      return {
        ok: false,
        stage: 'upload',
        issues: [
          {
            file: file.name,
            code: error instanceof AvatarBoError ? error.code : 'upload_failed',
            detail: error instanceof Error ? error.message : String(error),
          },
        ],
      }
    }
  }

  try {
    await client.registerOption({ ...option, uploadId })

    return { ok: true }
  } catch (error) {
    if (error instanceof AvatarBoError && error.issues.length) {
      return { ok: false, stage: 'register', issues: error.issues }
    }

    return {
      ok: false,
      stage: 'register',
      issues: [
        {
          code: error instanceof AvatarBoError ? error.code : 'register_failed',
          detail: error instanceof Error ? error.message : String(error),
        },
      ],
    }
  }
}

/**
 * One file, with a single retry on a fresh presigned URL.
 *
 * A presigned PUT is valid for 15 minutes. An admin who picks files, reads the check step and then
 * answers the phone comes back to a dead URL — retrying with a new one costs one request and turns
 * a confusing 403 into nothing at all. Only ONE retry: twice means the failure is not expiry.
 */
async function putOnce(
  client: AvatarBoClient,
  uploadId: string,
  file: File,
  onProgress: (loaded: number) => void,
): Promise<void> {
  const presigned = await client.presignUpload(uploadId, file.name)
  try {
    await client.putToStaging(presigned, file, (loaded) => onProgress(loaded))
  } catch (error) {
    if (!isExpired(error)) {
      throw error
    }
    const fresh = await client.presignUpload(uploadId, file.name)
    await client.putToStaging(fresh, file, (loaded) => onProgress(loaded))
  }
}
